import { CSSInterpolation } from '@emotion/css/create-instance'
import React from 'react'

import type { ColorToken } from '../themes'
import {
  RowUtils,
  type StartElemCheckmarkProps,
  type StartElemIconProps,
  type StartElemNumberPogProps,
  type StartElemPogProps,
} from './Rows/RowUtils'
import { Type } from './Type'

type Props = (
  | StartElemCheckmarkProps
  | StartElemIconProps
  | StartElemPogProps
  | StartElemNumberPogProps
) & {
  overrideRootStyles?: CSSInterpolation
  /**
   * The main content of a ValuePropRow. Everything in
   * children is rendered within a Text component.
   */
  children: React.ReactNode
  /**
   * To align the content and bullet point.
   */
  align?: React.ComponentProps<typeof RowUtils.Section>['align']
  /**
   * Sets aria-label value of the container.
   */
  'aria-label'?: React.HTMLAttributes<HTMLDivElement>['aria-label']
  /**
   * Sets aria-describedby value of the container.
   */
  'aria-labelledby'?: React.HTMLAttributes<HTMLDivElement>['aria-labelledby']
  /**
   * Optional bolded text at the top of a ValuePropRow.
   */
  title?: string
  /**
   * Optional color for the title text.
   */
  titleColor?: ColorToken | null
}

/**
 * A Web Row for value propositions, meant to display copy
 * and marketing blurbs.
 */
export function ValuePropRow(props: Props) {
  const {
    align = 'center',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    children,
    overrideRootStyles,
    title,
    ...leadingContentProps
  } = props
  const { colors } = RowUtils.useRowColors(false)
  return (
    <div
      aria-label={
        !ariaLabelledBy && (ariaLabel || title) ? ariaLabel || title : undefined
      }
      aria-labelledby={ariaLabelledBy}
      css={overrideRootStyles}
    >
      <RowUtils.Section
        align={align}
        leadingContent={
          <div
            css={
              // NOTE(omer.demirkan): In design specs. To sensibly align
              // items in certain edge cases.
              align === 'top' && title
                ? {
                    marginTop: props.startElem === 'pog' ? '8px' : '12px',
                  }
                : undefined
            }
          >
            <RowUtils.LeadingContent {...leadingContentProps} />
          </div>
        }
        mainContent={
          <>
            {title && (
              <div>
                <Type.Text
                  color={props.titleColor ?? colors.primaryText}
                  isBold
                  size="M"
                >
                  {title}
                </Type.Text>
              </div>
            )}
            <Type.Text color={colors.primaryText} size="M">
              {children}
            </Type.Text>
          </>
        }
        overrideRootStyles={{ padding: '8px 0' }}
      />
    </div>
  )
}
